import { OrderStatus } from '@/coreDomainsModule/entities/arrival';
import { AlertType } from '@/coreDomainsModule/entities/hasRecommendOrders';
import { PartialDelivery } from './partialDelivery';
import { UserAccount } from './users';

/**
 * 発注
 * @param orderId - 発注ID
 * @param aggregateBusinessId - 取引ID
 * @param supplierName - 仕入先名
 * @param businessNumber - 取引番号
 * @param businessName - 取引名
 * @param package - パッケージ
 * @param manufacturer - 製造社
 * @param warehouse - 倉庫
 * @param orderNumber - 発注番号
 * @param createdOrderDate - 発注日
 * @param desiredDeliveryDate - 希望納期
 * @param estimatedTimeOfArrival - 納入日
 * @param orderAmount - 発注数量
 * @param orderStatus - 発注ステータス
 * @param outputDate - 出力日時
 * @param isOutOfStockExpected - 在庫切れの可能性
 * @param statusUpdateAt - ステータス更新日時
 * @param orderAlert - 発注アラート
 * @param orderNote - 発注メモ
 * @param orderGroupId - 発注グループId
 * @param orderGroupName - 発注グループ名
 * @param invoiceNumber - インボイス番号
 * @param destinationName - 仕向地
 * @param upperLimitQuantity - 上限数量
 * @param userCustomParameters - ユーザー任意項目（24/8/23 現状、顧客台帳ID用を想定）
 * @param partialDeliveries - 分納情報
 * @param lastUpdatedByUser - 最終更新者
 * @param updatedAt - 更新日時
 * @param reminderMailDataId - リマインドメールデータID
 * @param orderBasisId - 発注根拠ID
 * @param initialOrderAmount - 発注作成時の発注数量
 * @param initialDesiredDeliveryDate - 発注作成時の希望納期
 */
export interface Order {
  orderId: string;
  aggregateBusinessId: string;
  supplierNumber: string;
  supplierName: string;
  businessNumber: string;
  businessName: string;
  package: number;
  unit: string;
  manufacturer: string | undefined;
  warehouseNumber: string;
  warehouse: string;
  aggregateBusinessResponsibleUsers: UserAccount[];
  orderIssuedNumber: string | undefined;
  orderNumber: string;
  createdOrderDate: number;
  desiredDeliveryDate: number;
  estimatedTimeOfArrival: number;
  orderAmount: number;
  orderStatus: OrderStatus;
  outputDate: number | undefined;
  isOutOfStockExpected: boolean | undefined;
  statusUpdateAt: number | undefined;
  orderAlert: AlertType[];
  orderNote: string | undefined;
  orderGroupId: string | undefined;
  orderGroupName: string | undefined;
  invoiceNumber: string | undefined;
  destinationName: string | undefined;
  upperLimitQuantity: number | undefined;
  userCustomParameters: { [key: string]: string };
  partialDeliveries: PartialDelivery[];
  lastUpdatedByUser: UserAccount;
  updatedAt: number;
  reminderMailDataId: string | null;
  orderBasisId: string | undefined;
  initialOrderAmount: number | undefined;
  initialDesiredDeliveryDate: number | undefined;
}

/**
 * 発注ステータスのフィルター項目。これらはOrderStatusの一部
 * @param ApprovedAwaitingDeliveryDate - 承認済
 * @param ApprovalRequested - 承認依頼
 * @param QuantityConfirmed - 納期回答待ち
 * @param DeliveryDateConfirmed - 数量回答待ち
 * @param PartialDelivery - 分納
 * @param Confirmed - 確定
 * @param Delivered - 納入済
 * @param DeliveryDelayed - 納期遅延
 */
export interface IOrderStatusFilterItem {
  ApprovedAwaitingDeliveryDate: boolean;
  ApprovalRequested: boolean;
  QuantityConfirmed: boolean;
  DeliveryDateConfirmed: boolean;
  PartialDelivery: boolean;
  Confirmed: boolean;
  Delivered: boolean;
  DeliveryDelayed: boolean;
}

/**
 * Orderの型ガード
 * @param obj - 判定対象のオブジェクト
 */
export const isOrder = (obj: any): obj is Order => {
  return (
    obj.orderId !== undefined &&
    obj.aggregateBusinessId !== undefined &&
    obj.supplierName !== undefined &&
    obj.businessNumber !== undefined &&
    obj.businessName !== undefined &&
    obj.package !== undefined &&
    obj.warehouse !== undefined &&
    obj.orderNumber !== undefined &&
    obj.createdOrderDate !== undefined &&
    obj.desiredDeliveryDate !== undefined &&
    obj.estimatedTimeOfArrival !== undefined &&
    obj.orderAmount !== undefined &&
    obj.orderStatus !== undefined &&
    obj.orderAlert !== undefined &&
    obj.userCustomParameters !== undefined &&
    obj.partialDeliveries !== undefined &&
    obj.lastUpdatedByUser !== undefined &&
    obj.updatedAt !== undefined
  );
};
